import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";

interface TripCardMenuProps {
  /**
   * Named in the trigger's label, so a screen reader hears which trip the menu
   * belongs to instead of a column of identical "Trip options".
   */
  readonly tripName: string;
  readonly onRename: () => void;
  /** Returns false when the traveller backed out of the confirmation. */
  readonly onDelete: () => boolean;
}

/**
 * Rename / delete for one trip card.
 *
 * The keyboard behaviour here is the trip switcher's, moved rather than
 * rewritten: that dropdown was the only arrow-key-navigable thing on the phone
 * Trips surface, and the list that replaced it would otherwise have shipped
 * without any of it. Arrows cycle, Home and End jump to the ends, Escape closes
 * and hands focus back to the trigger, and Tab dismisses the menu and carries
 * on down the page.
 */
export function TripCardMenu({
  tripName,
  onRename,
  onDelete,
}: TripCardMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close on any pointer down outside the menu.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  const menuItems = () =>
    menuRef.current
      ? Array.from(menuRef.current.querySelectorAll<HTMLButtonElement>("button"))
      : [];

  // However the menu opened, focus lands on the first item so the arrow keys
  // have somewhere to start from.
  useEffect(() => {
    if (!open) return;
    menuItems()[0]?.focus();
  }, [open]);

  const closeAndRefocus = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = menuItems();
    if (items.length === 0) return;
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        items[(i + 1 + items.length) % items.length]?.focus();
        break;
      case "ArrowUp":
        e.preventDefault();
        items[(i - 1 + items.length) % items.length]?.focus();
        break;
      case "Home":
        e.preventDefault();
        items[0]?.focus();
        break;
      case "End":
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case "Escape":
        e.preventDefault();
        closeAndRefocus();
        break;
      case "Tab":
        // Per the menu pattern, Tab dismisses and lets focus flow naturally to
        // the next control.
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const onTriggerKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="mj-trip-menu" ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        data-no-drag
        className="mj-trip-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("trip.trip_options", {
          name: tripName,
          defaultValue: "Options for {{name}}",
        })}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <Icon name="ri-more-2-fill" aria-hidden="true" />
      </button>
      {open && (
        <div
          className="mj-trip-menu__popover"
          data-no-drag
          role="menu"
          ref={menuRef}
          onKeyDown={onMenuKeyDown}
        >
          <button
            type="button"
            role="menuitem"
            data-no-drag
            className="mj-trip-menu__item"
            onClick={() => {
              // No refocus: the rename field mounts autofocused, and taking the
              // trigger back first would only steal it for a frame.
              setOpen(false);
              onRename();
            }}
          >
            <Icon name="ri-pencil-line" aria-hidden="true" />
            {t("trip.rename_trip", { defaultValue: "Rename trip" })}
          </button>
          <button
            type="button"
            role="menuitem"
            data-no-drag
            className="mj-trip-menu__item mj-trip-menu__item--danger"
            onClick={() => {
              setOpen(false);
              // A cancelled confirmation leaves the card where it was, so the
              // trigger takes its focus back rather than dropping it on <body>.
              if (!onDelete()) triggerRef.current?.focus();
            }}
          >
            <Icon name="ri-delete-bin-line" aria-hidden="true" />
            {t("trip.delete_trip")}
          </button>
        </div>
      )}
    </div>
  );
}
